import React, { useEffect, useState } from "react";
import { t } from "i18next";
import { loadOcClientScript } from "../../../common/scriptSecurity";
import * as rpc from "../../../common/jsonRpcRoutes";

interface FeatureSentimentProps {
  /** Feature name reported to the sentiment widget (e.g. "Booking Journey"). */
  readonly featureName?: string;
  /** Authenticated user id, if any — falls back to a guest placeholder. */
  readonly userId?: string | null;
  /** Customer name, if any — falls back to a guest placeholder. */
  readonly customerName?: string | null;
  /**
   * Whether this instance is shown on the booking journey confirmation
   * (`/book`). Controls the label shown on the widget: booking journey shows
   * "Were you able to book easily?", while secure your booking and preorder
   * flows show "Let us know how your experience was today".
   */
  readonly isBookingJourney?: boolean;
}

const PRODUCT_NAME = "CollinsNewCheckout";

/**
 * FeatureSentiment — embeds the OpenComponents feature feedback collector
 * (`<oc-component>`) on confirmation screens.
 *
 * Fetches its configuration from the `/feature-sentiment/config` route and
 * renders nothing until valid config is available (or if the fetch fails).
 */
const FeatureSentiment: React.FC<FeatureSentimentProps> = ({
  featureName = "PaymentConfirmationLayout",
  userId: userIdProp,
  customerName: customerNameProp,
  isBookingJourney = false,
}) => {
  const [accountToken, setAccountToken] = useState("");
  const [featureSentimentBaseUrl, setFeatureSentimentBaseUrl] = useState<string | null>(null);
  const [sentimentApiHost, setSentimentApiHost] = useState<string | null>(null);

  useEffect(() => {
    loadOcClientScript().catch((err) => console.error("Failed to load OC client script", err));

    rpc
      .call("/feature-sentiment/config", {})
      .then((config: { url: string; accountToken: string; apiHost: string }) => {
        setFeatureSentimentBaseUrl(config.url);
        setAccountToken(config.accountToken);
        setSentimentApiHost(config.apiHost);
      })
      .catch((err: Error) => console.error("Failed to fetch feature sentiment config", err));
  }, []);

  const userId = userIdProp?.trim() ? userIdProp.trim() : "guest_user_id";
  const customerName = customerNameProp?.trim() ? customerNameProp.trim() : "Guest User";
  const label = isBookingJourney
    ? t("feature_sentiment_booking_journey_text", "Were you able to book easily?")
    : t("feature_sentiment_experience_text", "Let us know how your experience was today");
  const componentUrl =
    featureSentimentBaseUrl && accountToken && sentimentApiHost
      ? `${featureSentimentBaseUrl}` +
        `product=${encodeURIComponent(PRODUCT_NAME)}` +
        `&feature=${encodeURIComponent(featureName)}` +
        `&accountToken=${encodeURIComponent(accountToken)}` +
        `&customerName=${encodeURIComponent(customerName)}` +
        `&userId=${encodeURIComponent(userId)}` +
        `&apiHost=${encodeURIComponent(sentimentApiHost)}` +
        `&reDisplayAfterDays=0` +
        `&label=${encodeURIComponent(label)}` +
        `&collapsible=false` +
        `&labelPosition=top` +
        `&showTooltip=false` +
        `&dismissible=false`
      : null;

  if (!componentUrl) {
    return null;
  }

  return (
    <div className="gcw:flex gcw:items-center gcw:flex-col gcw:mb-4 feature-sentiment">
      <oc-component
        href={componentUrl}
        style={
          {
            "--feedback-icon-size": "24px",
            "--feedback-font-size": "16px",
            "--feedback-button-size": "44px",
          } as React.CSSProperties
        }
      ></oc-component>
    </div>
  );
};

export default FeatureSentiment;
